import mixpanel from 'mixpanel-browser'

import type { EventName, EventPayloadMap } from './events'

export interface InitAnalyticsOptions {
  token: string
  appName: string
  env: string
  appVersion: string
  debug?: boolean
}

interface AnalyticsState {
  isEnabled: boolean
  debug: boolean
}

const state: AnalyticsState = {
  isEnabled: false,
  debug: false,
}

export function initAnalytics(options: InitAnalyticsOptions): void {
  if (state.isEnabled) {
    console.warn('[analytics] already initialized — ignoring duplicate init')
    return
  }

  if (!options.token) {
    state.debug = options.debug ?? false
    if (state.debug) {
      console.info('[analytics] no token provided — running in no-op mode')
    }
    return
  }

  mixpanel.init(options.token, {
    debug: options.debug ?? false,
    track_pageview: false,
    persistence: 'localStorage',
  })

  mixpanel.register({
    app_name: options.appName,
    env: options.env,
    app_version: options.appVersion,
  })

  state.isEnabled = true
  state.debug = options.debug ?? false
}

export function identifyUser(userId: string): void {
  if (!state.isEnabled || !userId) return
  mixpanel.identify(userId)
}

export function track<K extends EventName>(event: K, payload: EventPayloadMap[K]): void {
  if (state.debug) {
    console.info('[analytics] track', event, payload)
  }
  if (!state.isEnabled) return
  mixpanel.track(event, payload)
}

export function resetAnalytics(): void {
  if (state.isEnabled) {
    mixpanel.reset()
  }
  state.isEnabled = false
  state.debug = false
}

export function getAnalyticsState(): Readonly<AnalyticsState> {
  return { ...state }
}
